import { useRef, useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { ButtonWithDropdown } from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import TransferSolid from '@simplywallst/ui-core/icons/TransferSolid';
import PriceUpOutline from '@simplywallst/ui-core/icons/PriceUpOutline';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';

import type { Watchlist, WatchlistItem } from '@/hooks/useWatchlist';
import useWatchlistMutations from '../../../../hooks/useWatchlistMutations';

const WatchlistNarrativeFairValueModal = loadable(
  () => import('@/pages/Watchlist/components/FairValue'),
  {
    resolveComponent: (c) => c.WatchlistNarrativeFairValueModal,
  }
);

const AddRemoveCompany = loadable(
  () => import('@/components/AddRemoveCompany')
);

type Options = 'set-fair-value' | 'move-to-portfolio';

interface Props {
  watchlistId: Watchlist['id'];
  watchlistItem: WatchlistItem;
}

export const DeprecatedMoreOptions = ({
  watchlistId,
  watchlistItem,
}: Props) => {
  const { removeCompany } = useWatchlistMutations();
  const [modal, setModal] = useState<Options | null>(null);

  const { company } = watchlistItem;

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'MoreOptions',
    companyId: company.id,
  });

  const { current: options } = useRef([
    {
      label: 'Set Fair Value',
      value: 'set-fair-value',
      icon: <PriceUpOutline />,
    },
    {
      label: 'Move to Portfolio',
      value: 'move-to-portfolio',
      icon: <TransferSolid />,
    },
    {
      label: 'Remove',
      value: 'remove-company',
      icon: <BinSolid />,
    },
  ]);

  const handleSelect = useCallback(
    (value: string) => {
      if (value === 'move-to-portfolio') {
        setModal('move-to-portfolio');
        trackEvent({
          action: 'click',
          modifier: 'moveToPortfolio',
        });
      } else if (value === 'set-fair-value') {
        setModal('set-fair-value');
        trackEvent({
          action: 'click',
          modifier: 'openFairValueModal',
        });
      } else if (value === 'remove-company') {
        removeCompany({
          id: watchlistItem.companyId,
          includeSnackBar: true,
          watchlistId,
        });
        trackEvent({
          action: 'click',
          modifier: 'removeCompany',
        });
      }
    },
    [removeCompany, trackEvent, watchlistId, watchlistItem]
  );

  const handleCloseModal = useCallback(() => {
    setModal(null);
  }, []);

  const handleOnAddCompanyToPortfolio = useCallback(() => {
    handleCloseModal();
    removeCompany({
      id: company.id,
      includeSnackBar: true,
      message: 'Moved to portfolio',
      watchlistId,
    });
  }, [removeCompany, handleCloseModal, company.id, watchlistId]);

  return (
    <Track>
      <ButtonWithDropdown
        styleType="passive"
        data-cy-id="watchlist-more"
        options={options}
        onSelect={handleSelect}
      >
        <MoreHorizontalSolid />
      </ButtonWithDropdown>
      {modal === 'move-to-portfolio' && (
        <AddRemoveCompany
          companyId={company.id}
          tickerSymbol={company.tickerSymbol}
          uniqueSymbol={company.uniqueSymbol}
          title={`Move ${company.tickerSymbol} to Portfolio`}
          onClose={handleCloseModal}
          onAddCompany={handleOnAddCompanyToPortfolio}
        />
      )}
      {modal === 'set-fair-value' && (
        <WatchlistNarrativeFairValueModal
          isOpen
          onClose={handleCloseModal}
          companyId={company.id}
        />
      )}
    </Track>
  );
};
